import {
  ChangeDetectionStrategy,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { ChangeEvent } from '@ckeditor/ckeditor5-angular';
import {
  AccessibilityHelp,
  Alignment,
  Autoformat,
  AutoImage,
  Autosave,
  BalloonToolbar,
  BlockQuote,
  Bold,
  ClassicEditor,
  Code,
  CodeBlock,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  HorizontalLine,
  HtmlEmbed,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  ShowBlocks,
  SourceEditing,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
  Undo,
} from 'ckeditor5';
import { Base64ClampedSizeImageUploadAdapter } from './upload-adapter';

export interface RichTextEditorConfig {
  hideToolbar?: boolean;
  hideBorder?: boolean;
  showContentTopBorder?: boolean;
  squareContentBottomBorder?: boolean;
  licenseKey?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['rich-text-editor.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: RichTextEditorComponent,
    },
  ],
})
export class RichTextEditorComponent implements OnInit, ControlValueAccessor, AfterViewInit {
  /** Set once the view is torn down, so the async host-attach callback can no-op safely. */
  private destroyed = false;

  constructor(
    private injector: Injector,
    private changeDetector: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
    private destroyRef: DestroyRef
  ) {
    // Track teardown so the async `waitForHostAttached` callback never touches change detection
    // on a destroyed view. The note dialogs swap two editor instances via dueling `*ngIf`s on
    // form-state change, so destroy-mid-init is a normal interaction path, not an edge case.
    this.destroyRef.onDestroy(() => (this.destroyed = true));
  }

  @HostBinding('class') styles = 'theme-primary';

  @Input() disabled = false;
  @Input() allowMediaForm = false;
  @Input() maxLength: number | null = null;
  @Input() config?: RichTextEditorConfig;
  @Input() placeholder?: string;

  @Input() value = '';

  editor = ClassicEditor;
  layoutIsReady = false;

  touched = false;

  control?: NgControl;

  // Use a loose type here to allow passing through CKEditor configuration
  // options that might not yet be reflected in the EditorConfig typings.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected _config: any = {};

  get hideToolbar() {
    return !!this.config?.hideToolbar;
  }

  get hideBorder() {
    return !!this.config?.hideBorder;
  }

  get showContentTopBorder() {
    return !!this.config?.showContentTopBorder;
  }

  get squareContentBottomBorder() {
    return !!this.config?.squareContentBottomBorder;
  }

  onChange = (_value: string) => {
    // noop
  };

  onTouched = () => {
    // noop
  };

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(onChange: (value: string) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  editorChanged({ editor }: ChangeEvent) {
    if (!editor) return;

    const currentValue = editor.getData();
    this.onChange(currentValue);
    this.markAsTouched();
  }

  ngOnInit() {
    if (this.disabled) return;

    this.control = this.injector.get(NgControl);
    this.control.valueAccessor = this;
  }

  ngAfterViewInit() {
    this._config = {
      ...this.config,
      licenseKey: this.config?.licenseKey ?? 'GPL',
      placeholder: this.placeholder || undefined,
      toolbar: {
        items: [
          'undo',
          'redo',
          '|',
          'heading',
          'bold',
          'italic',
          '|',
          'fontSize',
          'fontFamily',
          'fontColor',
          'fontBackgroundColor',
          '|',
          'underline',
          'strikethrough',
          'subscript',
          'superscript',
          'code',
          '|',
          'horizontalLine',
          'link',
          'insertImage',
          ...(this.allowMediaForm ? ['mediaEmbed'] : []),
          'insertTable',
          'blockQuote',
          'codeBlock',
          'htmlEmbed',
          '|',
          'alignment',
          '|',
          'bulletedList',
          'numberedList',
          'todoList',
          'outdent',
          'indent',
          '|',
          'sourceEditing',
          'showBlocks',
        ],
        shouldNotGroupWhenFull: false,
      },
      plugins: [
        Base64ClampedSizeImageUploadAdapter,
        ...(this.allowMediaForm ? [MediaEmbed] : []),
        AccessibilityHelp,
        Alignment,
        Autoformat,
        AutoImage,
        Autosave,
        BalloonToolbar,
        BlockQuote,
        Bold,
        Code,
        CodeBlock,
        Essentials,
        FontBackgroundColor,
        FontColor,
        FontFamily,
        FontSize,
        GeneralHtmlSupport,
        Heading,
        HorizontalLine,
        HtmlEmbed,
        ImageBlock,
        ImageCaption,
        ImageInline,
        ImageInsert,
        ImageInsertViaUrl,
        ImageResize,
        ImageStyle,
        ImageTextAlternative,
        ImageToolbar,
        ImageUpload,
        Indent,
        IndentBlock,
        Italic,
        Link,
        LinkImage,
        List,
        ListProperties,
        Paragraph,
        PasteFromOffice,
        SelectAll,
        ShowBlocks,
        SourceEditing,
        Strikethrough,
        Style,
        Subscript,
        Superscript,
        Table,
        TableCaption,
        TableCellProperties,
        TableColumnResize,
        TableProperties,
        TableToolbar,
        TextTransformation,
        TodoList,
        Underline,
        Undo,
      ],
      balloonToolbar: ['bold', 'italic', '|', 'link', 'insertImage', '|', 'bulletedList', 'numberedList'],
      fontFamily: {
        supportAllValues: true,
      },
      fontSize: {
        options: [10, 12, 14, 'default', 18, 20, 22],
        supportAllValues: true,
      },
      heading: {
        options: [
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_paragraph',
          },
          {
            model: 'heading1',
            view: 'h1',
            title: 'Heading 1',
            class: 'ck-heading_heading1',
          },
          {
            model: 'heading2',
            view: 'h2',
            title: 'Heading 2',
            class: 'ck-heading_heading2',
          },
          {
            model: 'heading3',
            view: 'h3',
            title: 'Heading 3',
            class: 'ck-heading_heading3',
          },
          {
            model: 'heading4',
            view: 'h4',
            title: 'Heading 4',
            class: 'ck-heading_heading4',
          },
          {
            model: 'heading5',
            view: 'h5',
            title: 'Heading 5',
            class: 'ck-heading_heading5',
          },
          {
            model: 'heading6',
            view: 'h6',
            title: 'Heading 6',
            class: 'ck-heading_heading6',
          },
        ],
      },
      htmlSupport: {
        allow: [
          {
            name: /^.*$/,
            styles: true,
            attributes: true,
            classes: true,
          },
        ],
      },
      image: {
        toolbar: [
          'toggleImageCaption',
          'imageTextAlternative',
          '|',
          'imageStyle:inline',
          'imageStyle:wrapText',
          'imageStyle:breakText',
          '|',
          'resizeImage',
        ],
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
        decorators: {
          toggleDownloadable: {
            mode: 'manual',
            label: 'Downloadable',
            attributes: {
              download: 'file',
            },
          },
        },
      },
      list: {
        properties: {
          styles: true,
          startIndex: true,
          reversed: true,
        },
      },
      style: {
        definitions: [
          {
            name: 'Article category',
            element: 'h3',
            classes: ['category'],
          },
          {
            name: 'Title',
            element: 'h2',
            classes: ['document-title'],
          },
          {
            name: 'Subtitle',
            element: 'h3',
            classes: ['document-subtitle'],
          },
          {
            name: 'Info box',
            element: 'p',
            classes: ['info-box'],
          },
          {
            name: 'Side quote',
            element: 'blockquote',
            classes: ['side-quote'],
          },
          {
            name: 'Marker',
            element: 'span',
            classes: ['marker'],
          },
          {
            name: 'Spoiler',
            element: 'span',
            classes: ['spoiler'],
          },
          {
            name: 'Code (dark)',
            element: 'pre',
            classes: ['fancy-code', 'fancy-code-dark'],
          },
          {
            name: 'Code (bright)',
            element: 'pre',
            classes: ['fancy-code', 'fancy-code-bright'],
          },
        ],
      },
      table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'],
      },
    };

    /**
     * VS-968: defer the `layoutIsReady` flip until the host element is genuinely attached
     * to `document.body`.
     *
     * CKEditor 5.48+ requires the source element to be attached to `document` when `create()`
     * runs, otherwise it throws `editor-source-element-not-attached` and never initialises —
     * leaving a blank rectangle inside the dialog.
     *
     * `vs-dialog` uses `cdkConnectedOverlay`, so a dialog-hosted editor is created EAGERLY as
     * projected content (when the dialog component is constructed) but its host only enters the
     * live DOM when the user OPENS the dialog — which can be seconds or minutes later, or never.
     * We therefore observe `document.body` until the host actually connects (or the view is
     * destroyed) with NO fixed timeout: a fixed deadline would expire before the user opens the
     * dialog and permanently skip init, leaving the editor blank on open (regression). For
     * page-level hosts the element is already connected, so the observer never installs.
     *
     * Do not reintroduce a fixed `setTimeout`/single-cycle defer — attach timing is unbounded
     * (gated on user interaction). `DestroyRef` already prevents a leak if the host never attaches.
     */
    this.waitForHostAttached().then(() => {
      // Bail if the view was torn down while we were waiting — calling `detectChanges()` on a
      // destroyed view throws `ViewDestroyedError` (Angular 21). This is reachable in the note
      // dialogs that destroy/recreate the editor on form-state change, and whenever a dialog is
      // closed before it was ever opened/attached.
      if (this.destroyed || !this.elementRef.nativeElement.isConnected) return;
      this.layoutIsReady = true;
      this.changeDetector.detectChanges();
    });
  }

  /**
   * Resolves once the component's host element is connected to the live DOM, by observing
   * mutations on `document.body`. Also resolves (still-disconnected) if the view is destroyed
   * first, so the caller can bail without mounting. There is intentionally NO timeout — a
   * dialog-hosted editor may legitimately stay detached until the user opens the dialog.
   *
   * Uses `MutationObserver` instead of polling so we react to the exact DOM mutation that
   * inserts our element rather than asking 60×/second. The observer watches the whole subtree
   * because dialogs attach via nested portal containers, not directly under body.
   */
  private waitForHostAttached(): Promise<void> {
    return new Promise<void>(resolve => {
      // Fast path: most call sites (page-level hosts) are already connected — return immediately.
      if (this.elementRef.nativeElement.isConnected) {
        resolve();
        return;
      }

      let observer: MutationObserver | null = null;
      const finish = () => {
        observer?.disconnect();
        observer = null;
        resolve();
      };

      observer = new MutationObserver(() => {
        if (this.elementRef.nativeElement.isConnected) finish();
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Stop observing if the view is destroyed before it ever attaches (dialog closed without
      // being opened, fast open/close, etc.). The caller's `destroyed`/`isConnected` guard then
      // skips the mount. This is what makes the no-timeout approach leak-free.
      this.destroyRef.onDestroy(() => finish());

      // Close the setup race: if the host attached synchronously in the gap between the fast-path
      // check above and `observe()` registering, that mutation was never delivered to the observer.
      if (this.elementRef.nativeElement.isConnected) finish();
    });
  }
}
